/* localization-selector v1.2 — 2026-04-28 */
/**
 * Country / currency selector — vanilla, dependency-free.
 *
 * Pairs with snippets/country-localization.liquid. One instance per
 * `[data-localization]` wrapper. Multiple instances on the same page
 * (desktop + mobile) share a single backdrop and a single sprite fetch.
 *
 * Lazy-loads flags-sprite.svg on first drawer open across the page.
 * Inlines the sprite under <body> so <use href="#flag-XX"> resolves
 * same-document. Until then, .localization-flag nodes render as the
 * faint placeholder defined in CSS.
 *
 * No-JS fallback: drawer never opens, options inert. Storefront still
 * functions; geolocation-based market detection continues to work.
 */
(function () {
  'use strict';

  if (typeof document === 'undefined') return;

  var BODY_LOCK_CLASS = 'has-localization-open';
  var DRAWER_OPEN_CLASS = 'is-open';
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), [tabindex]:not([tabindex="-1"])';

  var spritePromise = null;
  var backdrop = null;
  var openDrawer = null;

  // Single shared backdrop — created once, toggled by whichever drawer opens.
  function ensureBackdrop() {
    if (backdrop) return backdrop;
    backdrop = document.createElement('div');
    backdrop.className = 'localization-backdrop';
    backdrop.setAttribute('aria-hidden', 'true');
    backdrop.addEventListener('click', function () {
      if (openDrawer) openDrawer.close();
    });
    document.body.appendChild(backdrop);
    return backdrop;
  }

  // Fetch the sprite once, inline under <body>, then patch every
  // .localization-flag[data-flag] with a <use href="#flag-xx"> child.
  function loadSprite(url) {
    if (spritePromise) return spritePromise;
    if (!url) return Promise.reject(new Error('no sprite url'));

    spritePromise = fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('sprite ' + res.status);
        return res.text();
      })
      .then(function (svgText) {
        var holder = document.createElement('div');
        holder.innerHTML = svgText;
        var sprite = holder.querySelector('svg');
        if (!sprite) throw new Error('sprite has no <svg>');
        sprite.style.display = 'none';
        sprite.setAttribute('aria-hidden', 'true');
        document.body.insertBefore(sprite, document.body.firstChild);
        populateFlags(document);
      })
      .catch(function (err) {
        // Reset so a later open retries. Country names still render
        // without flags — degraded but functional.
        spritePromise = null;
        if (window.console && console.warn) {
          console.warn('[localization] sprite load failed:', err);
        }
      });

    return spritePromise;
  }

  function populateFlags(scope) {
    var nodes = scope.querySelectorAll('.localization-flag[data-flag]');
    var SVG_NS = 'http://www.w3.org/2000/svg';
    var XLINK_NS = 'http://www.w3.org/1999/xlink';
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.firstChild) continue; // already populated
      var code = el.getAttribute('data-flag');
      if (!code) continue;
      var use = document.createElementNS(SVG_NS, 'use');
      use.setAttribute('href', '#flag-' + code);
      // Older Safari needs xlink:href as a fallback.
      use.setAttributeNS(XLINK_NS, 'xlink:href', '#flag-' + code);
      el.appendChild(use);
    }
  }

  function Localization(root) {
    this.root = root;
    this.trigger = root.querySelector('[data-localization-trigger]');
    this.drawer = root.querySelector('[data-localization-drawer]');
    this.closeBtn = root.querySelector('[data-localization-close]');
    this.search = root.querySelector('[data-localization-search]');
    this.input = root.querySelector('[data-localization-input]');
    this.form = root.querySelector('form');
    this.options = root.querySelectorAll('[data-localization-option]');
    this.optionRows = root.querySelectorAll('[data-localization-option-row]');
    this.groupTitles = root.querySelectorAll('[data-localization-group]');
    this.groupLists = root.querySelectorAll('[data-localization-group-list]');
    this.spriteUrl = root.getAttribute('data-sprite-url');
    this.isDesktop = root.classList.contains('localization--desktop');
    this.lastFocus = null;
    this.isOpen = false;
    this.outsideClickHandler = null;

    if (!this.trigger || !this.drawer || !this.form) return;

    // Move the drawer to <body> so it sits in the same stacking context
    // as the body-level backdrop. Otherwise the parent (mega menu /
    // siteheader-last) creates its own stacking context and the drawer's
    // z-index becomes relative to that — leaving the body-level backdrop
    // visually on top of the drawer and intercepting all clicks.
    if (this.drawer.parentNode !== document.body) {
      document.body.appendChild(this.drawer);
    }

    this.bind();
  }

  Localization.prototype.bind = function () {
    var self = this;

    // Stop trigger pointer/touch events from bubbling to `document` at all.
    // Two reasons:
    //   1) application-min.js wires a delegated body `click.megaBackground`
    //      handler that closes the mega menu on any body click.
    //   2) The desktop outside-click listener is attached to `document`
    //      mousedown/touchstart. Without these guards, the same press that
    //      triggers a "close" toggle below could also race the
    //      outside-click handler. Stopping at the trigger eliminates the
    //      race entirely.
    this.trigger.addEventListener('mousedown', function (e) {
      e.stopPropagation();
    });
    this.trigger.addEventListener('touchstart', function (e) {
      e.stopPropagation();
    }, { passive: true });

    this.trigger.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      // Toggle: a second click on the trigger should dismiss the dropdown
      // instead of just re-opening it.
      if (self.isOpen) {
        self.close();
      } else {
        self.open();
      }
    });

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        self.close();
      });
    }

    if (this.search) {
      this.search.addEventListener('input', function () {
        self.filter(self.search.value);
      });
    }

    for (var i = 0; i < this.options.length; i++) {
      this.options[i].addEventListener('click', function (e) {
        e.preventDefault();
        var code = this.getAttribute('data-value');
        if (!code) return;
        self.select(code);
      });
    }

    this.drawer.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        e.preventDefault();
        self.close();
        return;
      }
      if (e.key === 'Tab' || e.keyCode === 9) {
        self.trapFocus(e);
      }
    });
  };

  Localization.prototype.open = function () {
    if (this.isOpen) return;

    // Close any other drawer that might already be open (e.g., user
    // resizes mid-interaction).
    if (openDrawer && openDrawer !== this) openDrawer.close();

    var self = this;
    this.lastFocus = document.activeElement;
    this.isOpen = true;
    openDrawer = this;

    // Desktop is a lightweight popover (no dim, page stays scrollable, closes
    // on click-outside). Mobile keeps the modal drawer behaviour.
    ensureBackdrop();
    backdrop.classList.add(DRAWER_OPEN_CLASS);
    document.body.classList.add(BODY_LOCK_CLASS);
    if (this.isDesktop) {
      document.body.classList.add('has-localization-desktop');
    }

    this.drawer.classList.add(DRAWER_OPEN_CLASS);
    this.drawer.setAttribute('aria-hidden', 'false');
    if (!this.isDesktop) {
      this.drawer.setAttribute('aria-modal', 'true');
    }
    this.trigger.setAttribute('aria-expanded', 'true');

    // Lazy-load sprite on first open across the entire page.
    loadSprite(this.spriteUrl);

    if (this.isDesktop) {
      // Defer adding the document listener so this same click that opened the
      // dropdown isn't picked up by it. Ignore clicks inside the trigger
      // wrapper or the drawer itself.
      this.outsideClickHandler = function (e) {
        if (self.root.contains(e.target) || self.drawer.contains(e.target)) {
          return;
        }
        self.close();
      };
      setTimeout(function () {
        if (self.isOpen && self.outsideClickHandler) {
          document.addEventListener('mousedown', self.outsideClickHandler);
          document.addEventListener('touchstart', self.outsideClickHandler, { passive: true });
        }
      }, 0);
    }

    // Move focus into the drawer after the slide-in finishes so the
    // search input doesn't pop the mobile keyboard before the panel
    // visually arrives. 320ms matches the CSS transition.
    setTimeout(function () {
      if (!self.isOpen) return;
      if (self.search) {
        self.search.focus({ preventScroll: true });
      } else if (self.closeBtn) {
        self.closeBtn.focus({ preventScroll: true });
      }
    }, 320);
  };

  Localization.prototype.close = function () {
    if (!this.isOpen) return;
    this.isOpen = false;
    if (openDrawer === this) openDrawer = null;

    this.drawer.classList.remove(DRAWER_OPEN_CLASS);
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.removeAttribute('aria-modal');
    this.trigger.setAttribute('aria-expanded', 'false');

    if (backdrop) backdrop.classList.remove(DRAWER_OPEN_CLASS);
    document.body.classList.remove(BODY_LOCK_CLASS);
    document.body.classList.remove('has-localization-desktop');

    if (this.outsideClickHandler) {
      document.removeEventListener('mousedown', this.outsideClickHandler);
      document.removeEventListener('touchstart', this.outsideClickHandler);
      this.outsideClickHandler = null;
    }

    // Reset filter so the list is whole on next open.
    if (this.search && this.search.value) {
      this.search.value = '';
      this.filter('');
    }

    // Return focus to the trigger that opened the drawer.
    if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
      this.lastFocus.focus({ preventScroll: true });
    }
    this.lastFocus = null;
  };

  Localization.prototype.filter = function (term) {
    var q = (term || '').trim().toLowerCase();
    var matchCounts = {};

    for (var i = 0; i < this.optionRows.length; i++) {
      var li = this.optionRows[i];
      var hay = li.getAttribute('data-localization-search-term') || '';
      var match = !q || hay.indexOf(q) !== -1;
      li.hidden = !match;
      if (match) {
        // Walk up to the nearest [data-localization-group-list] so we
        // can hide empty group titles below.
        var list = li.parentNode;
        while (list && !list.hasAttribute('data-localization-group-list')) {
          list = list.parentNode;
        }
        if (list) {
          var key = list.getAttribute('data-localization-group-list');
          matchCounts[key] = (matchCounts[key] || 0) + 1;
        }
      }
    }

    // Hide group titles whose list has no visible items.
    for (var j = 0; j < this.groupTitles.length; j++) {
      var title = this.groupTitles[j];
      var key2 = title.getAttribute('data-localization-group');
      title.hidden = !matchCounts[key2];
    }
    for (var k = 0; k < this.groupLists.length; k++) {
      var list2 = this.groupLists[k];
      var key3 = list2.getAttribute('data-localization-group-list');
      list2.hidden = !matchCounts[key3];
    }
  };

  Localization.prototype.select = function (code) {
    if (!this.input) return;
    this.input.value = code;
    // Native form submission — Shopify writes the cart_currency /
    // localization cookies and 302s back to return_to.
    this.form.submit();
  };

  Localization.prototype.trapFocus = function (e) {
    var focusables = this.drawer.querySelectorAll(FOCUSABLE);
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    var active = document.activeElement;

    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  function init() {
    var roots = document.querySelectorAll('[data-localization]');
    for (var i = 0; i < roots.length; i++) {
      new Localization(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
